import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import BuyButton from "@/components/BuyButton";
import Seo from "@/components/Seo";
import { COLLECTIONS, EXTRA_BUCKS, BUCKS_IMAGE, PRICES } from "@/data/content";

const CollectionDetail = () => {
  const { collectionId } = useParams();
  const collection = COLLECTIONS.find((c) => c.id === collectionId);

  if (!collection) {
    return (
      <div className="bg-cream">
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-heading font-bold text-3xl text-ink mb-4">Collection not found</h1>
          <Link to="/collections" data-testid="detail-notfound-back-link" className="text-sage font-semibold underline underline-offset-4">
            Explore UNSTUCK Collections
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`collection-detail-${collection.id}`} className="bg-cream">
      <Seo
        title={`UNSTUCK ${collection.name} Deck — 15-Minute Activity Cards`}
        description={collection.long}
        path={`/collections/${collection.id}`}
      />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16">
        <Link
          to="/collections"
          data-testid="detail-back-link"
          className="inline-flex items-center gap-2 text-sage font-semibold hover:text-sage-dark transition-colors mb-10"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" /> All UNSTUCK Collections
        </Link>

        <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
          <div className="rounded-2xl overflow-hidden bg-sage-light shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
            <img
              src={collection.image}
              alt={`UNSTUCK ${collection.name} deck and activity cards`}
              data-testid="detail-image"
              className="w-full aspect-[4/3] object-cover"
              loading="eager"
            />
          </div>
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">
              UNSTUCK Collections
            </p>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight leading-tight text-ink mb-4">
              UNSTUCK {collection.name}
            </h1>
            <p className="font-heading font-black text-4xl text-sage mb-8" data-testid="detail-price">
              ${collection.price.toFixed(2)} <span className="text-lg font-body font-medium text-ink/60">+ shipping</span>
            </p>
            <p className="text-lg sm:text-xl leading-relaxed text-ink/80 mb-4">{collection.short}</p>
            <p className="text-lg leading-relaxed text-ink/80 mb-8">{collection.long}</p>
            <ul className="space-y-3 mb-8">
              {[
                "54 simple activity cards in one deck",
                "Pull one card, give it up to 15 minutes — one small step counts",
                "Use it alone or together with someone else",
                "A thoughtful gift that fits in a bag, a drawer, or a greeting",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-base text-ink/85">
                  <Check className="h-5 w-5 text-sage shrink-0 mt-0.5" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
            <BuyButton
              lookupKey={collection.lookupKey}
              testId="detail-buy-button"
              className="w-full sm:w-auto bg-sage text-white hover:bg-sage-dark shadow-[0_2px_10px_rgba(74,93,78,0.2)]"
            >
              Buy This Deck — ${collection.price.toFixed(2)}
            </BuyButton>
            <p className="mt-4 text-base text-ink/70" data-testid="detail-shipping-note">
              Ships within 3–5 business days. Flat $12.00 shipping per order.
            </p>
          </div>
        </div>

        {/* Extra UNSTUCK Bucks cross-sell */}
        <div className="mt-20 bg-white rounded-2xl border border-border shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-8 sm:p-10 grid gap-8 lg:grid-cols-2 lg:items-center" data-testid="detail-bucks-addon">
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-3">
              Optional add-on
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-ink mb-4">
              Add the UNSTUCK Bucks reward system
            </h2>
            <p className="text-lg leading-relaxed text-ink/80 mb-4">
              Extra UNSTUCK Bucks is a digital printable PDF that adds the optional reward system to any
              UNSTUCK deck — print as many as you need for personal or internal program use. Delivered by
              download right after purchase; nothing ships.
            </p>
            <p className="text-sm uppercase tracking-widest font-semibold text-ink/60 mb-2">
              Game use only • No cash value
            </p>
            <p className="text-sm text-ink/60 mb-6">
              Personal or internal program use only. No resale or redistribution.
            </p>
            <p className="font-heading font-black text-3xl text-sage mb-6">
              ${EXTRA_BUCKS.price.toFixed(2)} <span className="text-lg font-body font-medium text-ink/60">digital download</span>
            </p>
            <BuyButton
              lookupKey={EXTRA_BUCKS.lookupKey}
              testId="detail-buy-bucks-button"
              className="bg-gold text-ink hover:bg-gold-dark hover:text-white"
            >
              Add Extra UNSTUCK Bucks — ${EXTRA_BUCKS.price.toFixed(2)}
            </BuyButton>
          </div>
          <div className="rounded-2xl overflow-hidden bg-sage-light">
            <img
              src={BUCKS_IMAGE}
              alt="Sheet of printable UNSTUCK Bucks in $1, $5, $10 and $20 designs"
              data-testid="detail-bucks-image"
              className="w-full aspect-[4/3] object-cover"
              loading="lazy"
            />
          </div>
        </div>

        {/* Seniors cross-link */}
        <div className="mt-14 bg-sage rounded-2xl p-10 sm:p-12 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
          <div className="max-w-2xl">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-3">
              Looking for our featured collection?
            </h2>
            <p className="text-lg text-cream/90">
              UNSTUCK Seniors is available as a complete 4-deck program for ${PRICES.seniorsProgram.toFixed(0)} or a
              single deck for ${PRICES.seniorsDeck.toFixed(2)}.
            </p>
          </div>
          <Link
            to="/seniors"
            data-testid="detail-seniors-cta"
            className="inline-flex shrink-0 items-center justify-center gap-2 bg-gold text-ink hover:bg-gold-dark hover:text-white rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-sage"
          >
            Shop UNSTUCK Seniors <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CollectionDetail;
